import React, { useState, useEffect, useCallback } from 'react';
import { ArrowPathIcon, CloudArrowUpIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import DropZone from './DropZone';
import UploadProgress from './UploadProgress';
import DuplicateFilesDialog from './DuplicateFilesDialog';
import uploadsService from '../../services/uploads.service';
import {
  FileWithPreview,
  WebUploadResponse,
  SupportedFormatsResponse,
} from '../../types/uploads.types';

type UploadState = 'idle' | 'selected' | 'uploading' | 'complete' | 'error';

// Language codes for transcription (labels from translations)
const LANGUAGE_CODES = ['', 'en', 'es', 'pt', 'fr', 'de', 'it'];

const WebUploadSection: React.FC = () => {
  const { t } = useTranslation('uploads');
  const [uploadState, setUploadState] = useState<UploadState>('idle');
  const [files, setFiles] = useState<FileWithPreview[]>([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResult, setUploadResult] = useState<WebUploadResponse | undefined>();
  const [supportedFormats, setSupportedFormats] = useState<SupportedFormatsResponse | undefined>();
  const [error, setError] = useState<string | null>(null);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [duplicateFiles, setDuplicateFiles] = useState<string[]>([]);
  const [selectedLanguage, setSelectedLanguage] = useState<string>('');

  // Fetch supported formats on mount
  useEffect(() => {
    const fetchFormats = async () => {
      try {
        const formats = await uploadsService.getSupportedFormats();
        setSupportedFormats(formats);
      } catch (err) {
        console.error('Failed to fetch supported formats:', err);
      }
    };
    fetchFormats();
  }, []);

  const handleFilesSelected = useCallback((newFiles: FileWithPreview[]) => {
    setFiles((prev) => [...prev, ...newFiles]);
    setUploadState('selected');
    setError(null);
    setUploadResult(undefined);
  }, []);

  const handleRemoveFile = useCallback((fileId: string) => {
    setFiles((prev) => {
      const updated = prev.filter((f) => f.id !== fileId);
      if (updated.length === 0) {
        setUploadState('idle');
      }
      return updated;
    });
  }, []);

  const handleClear = useCallback(() => {
    setFiles([]);
    setUploadState('idle');
    setUploadProgress(0);
    setUploadResult(undefined);
    setError(null);
  }, []);

  const handleUpload = useCallback(async () => {
    if (files.length === 0) return;

    setUploadState('uploading');
    setError(null);
    setUploadProgress(0);

    // Update file statuses to uploading
    setFiles((prev) =>
      prev.map((f) => ({ ...f, status: 'uploading' as const }))
    );

    try {
      // Simulate progress updates
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 300);

      const result = await uploadsService.uploadFiles(
        files.map((f) => f.file),
        { language: selectedLanguage || undefined }
      );

      clearInterval(progressInterval);
      setUploadProgress(100);
      setUploadResult(result);

      // Update file statuses based on result
      setFiles((prev) =>
        prev.map((f) => {
          const fileResult = result.file_results.find(
            (r) => r.filename === f.file.name
          );
          return {
            ...f,
            status: fileResult?.status || 'completed',
            error: fileResult?.error,
          };
        })
      );

      setUploadState('complete');

      // Check for duplicates in results
      const duplicates = result.file_results
        .filter(r => r.status === 'skipped' && r.error?.includes('Duplicate'))
        .map(r => r.filename);

      if (duplicates.length > 0) {
        setDuplicateFiles(duplicates);
        setShowDuplicateDialog(true);
      }
    } catch (err: any) {
      console.error('Upload failed:', err);
      setError(err.response?.data?.detail || err.message || t('webUpload.uploadFailed'));
      setUploadState('error');
      setFiles((prev) =>
        prev.map((f) => ({ ...f, status: 'failed' as const }))
      );
    }
  }, [files, t]);

  const handleRetry = useCallback(() => {
    // Reset failed files to pending and retry
    setFiles((prev) =>
      prev
        .filter((f) => f.status === 'rejected' || f.status === 'failed')
        .map((f) => ({ ...f, status: 'pending' as const, error: undefined }))
    );
    setUploadState('selected');
    setUploadResult(undefined);
  }, []);

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden">
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
        <div className="flex items-center">
          <CloudArrowUpIcon className="h-6 w-6 text-indigo-500 mr-3" />
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              {t('webUpload.title')}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {t('webUpload.description')}
            </p>
          </div>
        </div>
      </div>

      <div className="px-4 py-5 sm:p-6">
        {/* Error message */}
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  {t('webUpload.uploadFailed')}
                </h3>
                <p className="mt-1 text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Language selector */}
        {(uploadState === 'idle' || uploadState === 'selected') && (
          <div className="mb-4">
            <label htmlFor="language-select" className="block text-sm font-medium text-gray-700 mb-1">
              {t('webUpload.languageLabel')}
            </label>
            <select
              id="language-select"
              value={selectedLanguage}
              onChange={(e) => setSelectedLanguage(e.target.value)}
              className="block w-full max-w-xs rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            >
              {LANGUAGE_CODES.map((code) => (
                <option key={code || 'auto'} value={code}>
                  {t(`webUpload.languages.${code || 'auto'}`)}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              {t('webUpload.languageHint')}
            </p>
          </div>
        )}

        {/* Drop zone (shown when idle or selecting) */}
        {(uploadState === 'idle' || uploadState === 'selected') && (
          <DropZone
            onFilesSelected={handleFilesSelected}
            supportedFormats={supportedFormats}
            disabled={false}
          />
        )}

        {/* Progress and file list */}
        {files.length > 0 && (
          <div className="mt-6">
            <UploadProgress
              files={files}
              isUploading={uploadState === 'uploading'}
              uploadProgress={uploadProgress}
              uploadResult={uploadResult}
              onCancel={uploadState === 'uploading' ? undefined : undefined} // TODO: Implement cancel
              onRetry={uploadState === 'complete' && (uploadResult?.files_rejected ?? 0) > 0 ? handleRetry : undefined}
              onRemoveFile={uploadState === 'selected' ? handleRemoveFile : undefined}
              onClear={uploadState === 'complete' || uploadState === 'error' ? handleClear : undefined}
            />
          </div>
        )}

        {/* Action buttons */}
        {uploadState === 'selected' && files.length > 0 && (
          <div className="mt-6 flex justify-end gap-3">
            <button
              onClick={handleClear}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              {t('webUpload.clearAll')}
            </button>
            <button
              onClick={handleUpload}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <CloudArrowUpIcon className="h-5 w-5 mr-2" />
              {t('webUpload.uploadButton', { count: files.length })}
            </button>
          </div>
        )}

        {/* Upload more button after completion */}
        {uploadState === 'complete' && (
          <div className="mt-6 flex justify-center">
            <button
              onClick={handleClear}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 border border-transparent rounded-md hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <ArrowPathIcon className="h-5 w-5 mr-2" />
              {t('webUpload.uploadMore')}
            </button>
          </div>
        )}
      </div>

      <DuplicateFilesDialog
        isOpen={showDuplicateDialog}
        onClose={() => setShowDuplicateDialog(false)}
        duplicateFiles={duplicateFiles}
      />
    </div>
  );
};

export default WebUploadSection;
